import {
  type ColorValue,
  CssColor,
  CssFont,
  type NumericValue,
  type PropertyTypeValueMap,
  StyleType,
  wixColor,
  wixFont,
} from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createStylesParam,
  createStylesParams,
  type ISettingsColor,
  type ISettingsFont,
  type ISiteTextPreset,
  type IStyleParam,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';

type CssColorValue = Parameters<typeof wixColor>[0];
type CssFontValue = Parameters<typeof wixFont>[0];

export type IStylesParams = {
  backgroundColor: StyleParamType.Color;
  textFont: StyleParamType.Font;
  textColor: StyleParamType.Color;
  topAndBottomPadding: StyleParamType.Number;
  sidePadding: StyleParamType.Number;
  formFieldsGap: StyleParamType.Number;
  widgetBackgroundColor: StyleParamType.Color;
  selectionColor: StyleParamType.Color;
  dividerWidth: StyleParamType.Number;
  dividerColorAndOpacity: StyleParamType.Color;
  widgetBorderWidth: StyleParamType.Number;
  widgetBorderColorAndOpacity: StyleParamType.Color;
  widgetBorderRadius: StyleParamType.Number;
  formBorderWidth: StyleParamType.Number;
  formBorderColor: StyleParamType.Color;
  formBorderRadius: StyleParamType.Number;
  formFieldBackgroundColor: StyleParamType.Color;
  sectionTitleColor: StyleParamType.Color;
  sectionTitleFont: StyleParamType.Font;
  primaryTextColor: StyleParamType.Color;
  primaryTextFont: StyleParamType.Font;
  secondaryTextColor: StyleParamType.Color;
  secondaryTextFont: StyleParamType.Font;
  priceBeforeDiscountColor: StyleParamType.Color;
  discountNameColor: StyleParamType.Color;
  discountNameFont: StyleParamType.Font;
  formFieldsLabelsColor: StyleParamType.Color;
  formFieldsLabelsFont: StyleParamType.Font;
  formFieldsInputTextColor: StyleParamType.Color;
  formFieldsInputTextFont: StyleParamType.Font;
  mobileServiceNameFontSize: StyleParamType.Number;
  mobileServiceTitlesFontSize: StyleParamType.Number;
  mobilePrimaryTextFontSize: StyleParamType.Number;
  mobileSecondaryTextFontSize: StyleParamType.Number;
  mobileFormFieldsLabelsFontSize: StyleParamType.Number;
  mobileFormFieldsInputsFontSize: StyleParamType.Number;
  mobileButtonSize: StyleParamType.Number;
  mainButtonTextColor: StyleParamType.Color;
  mainButtonFont: StyleParamType.Font;
  mainButtonBackgroundColorAndOpacity: StyleParamType.Color;
  mainButtonBorderColorAndOpacity: StyleParamType.Color;
  mainButtonBorderWidth: StyleParamType.Number;
  mainButtonBorderRadius: StyleParamType.Number;
  mainButtonHoverBackgroundColorAndOpacity: StyleParamType.Color;
  mainButtonHoverColorAndOpacity: StyleParamType.Color;
  mainButtonHoverTextColor: StyleParamType.Color;
  secondaryButtonTextColor: StyleParamType.Color;
  secondaryButtonFont: StyleParamType.Font;
  secondaryButtonBackgroundColorAndOpacity: StyleParamType.Color;
  secondaryButtonBorderColorAndOpacity: StyleParamType.Color;
  secondaryButtonBorderWidth: StyleParamType.Number;
  secondaryButtonBorderRadius: StyleParamType.Number;
  secondaryButtonHoverBackgroundColorAndOpacity: StyleParamType.Color;
  secondaryButtonHoverColorAndOpacity: StyleParamType.Color;
  secondaryButtonHoverTextColor: StyleParamType.Color;
  formHeaderTwoFont: StyleParamType.Font;
  formHeaderTwoColor: StyleParamType.Color;
  formTextFont: StyleParamType.Font;
  formTextColor: StyleParamType.Color;
  pageTitleFont: StyleParamType.Font;
  pageTitleColor: StyleParamType.Color;
  serviceDetailsFont: StyleParamType.Font;
  serviceDetailsColor: StyleParamType.Color;
  secondaryTextFormFont: StyleParamType.Font;
  secondaryTextFormColor: StyleParamType.Color;
  disableColor: StyleParamType.Color;
};

type NumberStyleParam = Omit<
  IStyleParam<StyleParamType.Number, number>,
  'name'
>;

type StyleTypeFor<ValueType> = keyof {
  [K in keyof PropertyTypeValueMap as Required<
    PropertyTypeValueMap[K]
  > extends Required<ValueType>
    ? K
    : never]: K;
};

const numberParam = (
  type: StyleTypeFor<NumericValue>,
  defaultValue: number | undefined,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: () => defaultValue as number,
  metadata: {
    type: type as StyleType.UNKNOWN_CssPropertyType, // type assertion is needed to avoid strange TypeScript error, because the enum union is too long
    defaultValue: {
      type: 'static',
      value: defaultValue as number,
    },
  },
});

const numberParamWithoutInheritDesktop = (
  type: StyleTypeFor<NumericValue>,
  defaultValue: number,
): NumberStyleParam => ({
  inheritDesktop: false,
  ...numberParam(type, defaultValue),
});

const numberRefParam = (
  type: StyleTypeFor<NumericValue>,
  otherParam: NumberStyleParam,
  otherParamName: string,
  fallback: number,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: ({ getStyleParamValue }) =>
    getStyleParamValue(otherParam) ?? fallback,
  metadata: {
    type: type as StyleType.UNKNOWN_CssPropertyType, // type assertion is needed to avoid strange TypeScript error, because the enum union is too long
    defaultValue: {
      type: 'ref',
      ref: otherParamName,
      fallback: {
        type: 'static',
        value: fallback,
      },
    },
  },
});

type ColorStyleParam = Omit<
  IStyleParam<StyleParamType.Color, ISettingsColor>,
  'name'
>;

const colorParam = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
  themeColor: CssColorValue,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset),
  metadata: {
    type,
    defaultValue: wixColor(themeColor),
  },
});

const colorRefParam = (
  type: StyleTypeFor<ColorValue>,
  otherColorParam: ColorStyleParam,
  otherColorParamName: string,
  themeColor?: CssColorValue,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: ({ getStyleParamValue }) =>
    getStyleParamValue(otherColorParam),
  metadata: {
    type,
    defaultValue: themeColor
      ? wixColor(themeColor)
      : {
          type: 'ref',
          ref: otherColorParamName,
        },
  },
});

const colorParamWithOpacity = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
  opacity: number,
  themeColor: CssColorValue,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset, opacity),
  metadata: {
    type,
    defaultValue: wixColor(themeColor),
  },
});

type FontStyleParam = Omit<
  IStyleParam<StyleParamType.Font, ISettingsFont>,
  'name'
>;

const fontParam = (
  preset: keyof ISiteTextPreset,
  props: { size?: number; htmlTag?: string },
  themeFont: CssFontValue,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam(preset, props),
  metadata: {
    type: StyleType.font,
    defaultValue: wixFont(themeFont),
  },
});

const fontRefParam = (
  otherFontParam: FontStyleParam,
  otherFontParamName: string,
  themeFont?: CssFontValue,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: ({ getStyleParamValue }) =>
    getStyleParamValue(otherFontParam),
  metadata: {
    type: StyleType.font,
    defaultValue: themeFont
      ? wixFont(themeFont)
      : {
          type: 'ref',
          ref: otherFontParamName,
        },
  },
});

const fontRefParamWithMobileFallback = (
  otherFontParam: FontStyleParam,
  otherFontParamName: string,
  themeFont?: CssFontValue,
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: ({ getStyleParamValue }) =>
    getStyleParamValue(otherFontParam),
  metadata: {
    type: StyleType.font,
    defaultValue: themeFont
      ? wixFont(themeFont)
      : {
          type: 'ref',
          ref: otherFontParamName,
        },
    mobileDefaultValue: themeFont
      ? wixFont(themeFont)
      : {
          type: 'ref',
          ref: otherFontParamName,
        },
  },
});

const widgetBackgroundColor = createStylesParam(
  'widgetBackgroundColor',
  colorParam(
    StyleType.backgroundColor,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
);

const mainButtonBorderWidth = createStylesParam(
  'mainButtonBorderWidth',
  numberParam(StyleType.borderWidth, 1),
);

const mainButtonBorderRadius = createStylesParam(
  'mainButtonBorderRadius',
  numberParam(StyleType.borderRadius, 0),
);

const sectionTitleFont = createStylesParam(
  'sectionTitleFont',
  fontParam('Page-title', { size: 20 }, CssFont.WstHeading2Font),
);

const sectionTitleColor = createStylesParam(
  'sectionTitleColor',
  colorParam(StyleType.color, 'color-5', CssColor.WstParagraph2Color),
);

const primaryTextFont = createStylesParam(
  'primaryTextFont',
  fontParam('Body-M', { size: 16 }, CssFont.WstParagraph2Font),
);

const primaryTextColor = createStylesParam(
  'primaryTextColor',
  colorParam(StyleType.color, 'color-5', CssColor.WstParagraph2Color),
);

const secondaryTextFont = createStylesParam(
  'secondaryTextFont',
  fontParam('Body-M', { size: 14 }, CssFont.WstParagraph2Font),
);

const secondaryTextColor = createStylesParam(
  'secondaryTextColor',
  colorParam(StyleType.color, 'color-4', CssColor.WstShade3Color),
);

export default createStylesParams<IStylesParams>({
  formHeaderTwoFont: fontRefParam(
    sectionTitleFont,
    'sectionTitleFont',
    CssFont.WstHeading2Font,
  ),
  formHeaderTwoColor: colorRefParam(
    StyleType.color,
    sectionTitleColor,
    'sectionTitleColor',
    CssColor.WstHeading2Color,
  ),
  formTextFont: fontRefParamWithMobileFallback(
    primaryTextFont,
    'primaryTextFont',
    CssFont.WstParagraph2Font,
  ),
  formTextColor: colorRefParam(
    StyleType.color,
    primaryTextColor,
    'primaryTextColor',
    CssColor.WstParagraph2Color,
  ),
  pageTitleFont: fontParam('Page-title', { size: 32 }, CssFont.WstHeading2Font),
  pageTitleColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstHeading2Color,
  ),
  serviceDetailsFont: fontRefParam(
    sectionTitleFont,
    'sectionTitleFont',
    CssFont.WstHeading2Font,
  ),
  serviceDetailsColor: colorRefParam(
    StyleType.color,
    sectionTitleColor,
    'sectionTitleColor',
    CssColor.WstHeading2Color,
  ),
  secondaryTextFormFont: fontRefParam(
    secondaryTextFont,
    'secondaryTextFont',
    CssFont.WstParagraph2Font,
  ),
  secondaryTextFormColor: colorRefParam(
    StyleType.color,
    secondaryTextColor,
    'secondaryTextColor',
    CssColor.WstParagraph2Color,
  ),
  disableColor: colorParamWithOpacity(
    StyleType.color,
    'button-color-fill-primary-disabled',
    1,
    CssColor.WstSystemDisabledColor,
  ),
  backgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-3',
    CssColor.WstPrimaryBackgroundColor,
  ),
  textFont: fontParam('Body-M', { size: 20 }, CssFont.WstParagraph2Font),
  textColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  topAndBottomPadding: numberParamWithoutInheritDesktop(StyleType.padding, 48),
  sidePadding: numberParamWithoutInheritDesktop(StyleType.padding, 20),
  formFieldsGap: numberParamWithoutInheritDesktop(StyleType.gap, 24),
  widgetBackgroundColor,
  selectionColor: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstLinksAndActionsColor,
  ),
  dividerWidth: numberParam(StyleType.width, 1),
  dividerColorAndOpacity: colorParamWithOpacity(
    StyleType.color,
    'color-5',
    0.2,
    CssColor.WstSystemLine1Color,
  ),
  widgetBorderWidth: numberParam(StyleType.borderWidth, 0),
  widgetBorderColorAndOpacity: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    0.2,
    CssColor.WstSystemLine1Color,
  ),
  widgetBorderRadius: numberParam(StyleType.borderRadius, 0),
  formBorderWidth: numberParam(StyleType.borderWidth, 1),
  formBorderColor: colorParam(
    StyleType.borderColor,
    'color-5',
    CssColor.WstSystemLine1Color,
  ),
  formBorderRadius: numberParam(StyleType.borderRadius, 0),
  formFieldBackgroundColor: colorRefParam(
    StyleType.backgroundColor,
    widgetBackgroundColor,
    'widgetBackgroundColor',
    CssColor.WstPrimaryBackgroundColor,
  ),
  sectionTitleFont,
  sectionTitleColor,
  primaryTextFont,
  primaryTextColor,
  secondaryTextColor,
  secondaryTextFont,
  formFieldsLabelsColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  formFieldsLabelsFont: fontParam(
    'Body-M',
    { size: 16 },
    CssFont.WstParagraph2Font,
  ),
  formFieldsInputTextColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  formFieldsInputTextFont: fontParam(
    'Body-M',
    { size: 16 },
    CssFont.WstParagraph2Font,
  ),
  mobileServiceNameFontSize: numberParam(StyleType.fontSize, 32),
  mobileServiceTitlesFontSize: numberParam(StyleType.fontSize, 20),
  mobilePrimaryTextFontSize: numberParam(StyleType.fontSize, 16),
  mobileSecondaryTextFontSize: numberParam(StyleType.fontSize, 14),
  mobileFormFieldsLabelsFontSize: numberParam(StyleType.fontSize, 14),
  mobileFormFieldsInputsFontSize: numberParam(StyleType.fontSize, 14),
  mobileButtonSize: numberParam(StyleType.fontSize, 14),
  mainButtonTextColor: colorParam(
    StyleType.color,
    'color-1',
    CssColor.WstButtonPrimaryColor,
  ),
  mainButtonFont: fontParam('Body-M', { size: 16 }, CssFont.WstParagraph2Font),
  mainButtonBackgroundColorAndOpacity: colorParam(
    StyleType.backgroundColor,
    'color-8',
    CssColor.WstButtonPrimaryBackgroundColor,
  ),
  mainButtonBorderColorAndOpacity: colorParam(
    StyleType.borderColor,
    'color-8',
    CssColor.WstButtonPrimaryBorderRightColor,
  ),
  mainButtonBorderWidth,
  mainButtonBorderRadius,
  mainButtonHoverBackgroundColorAndOpacity: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    1,
    CssColor.WstButtonPrimaryBackgroundColor,
  ),
  mainButtonHoverColorAndOpacity: colorParamWithOpacity(
    StyleType.color,
    'color-8',
    1,
    CssColor.WstLinksAndActionsColor,
  ),
  mainButtonHoverTextColor: colorParam(
    StyleType.color,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  secondaryButtonTextColor: colorParam(
    StyleType.color,
    'color-1',
    CssColor.WstButtonPrimaryColor,
  ),
  secondaryButtonFont: fontParam(
    'Body-M',
    { size: 16 },
    CssFont.WstParagraph2Font,
  ),
  secondaryButtonBackgroundColorAndOpacity: colorParam(
    StyleType.backgroundColor,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  secondaryButtonBorderColorAndOpacity: colorParam(
    StyleType.borderColor,
    'color-5',
    CssColor.WstSystemLine1Color,
  ),
  secondaryButtonBorderWidth: numberRefParam(
    StyleType.borderWidth,
    mainButtonBorderWidth,
    'mainButtonBorderWidth',
    1,
  ),
  secondaryButtonBorderRadius: numberRefParam(
    StyleType.borderRadius,
    mainButtonBorderRadius,
    'mainButtonBorderRadius',
    0,
  ),
  secondaryButtonHoverBackgroundColorAndOpacity: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-5',
    1,
    CssColor.WstParagraph2Color,
  ),
  secondaryButtonHoverColorAndOpacity: colorParamWithOpacity(
    StyleType.borderColor,
    'color-5',
    1,
    CssColor.WstParagraph2Color,
  ),
  secondaryButtonHoverTextColor: colorParam(
    StyleType.color,
    'color-1',
    CssColor.WstParagraph2Color,
  ),
  priceBeforeDiscountColor: createStylesParam(
    'priceBeforeDiscountColor',
    colorParam(StyleType.color, 'color-3', CssColor.WstButtonSecondaryColor),
  ),
  discountNameFont: createStylesParam(
    'discountNameFont',
    fontParam('Body-M', { size: 14 }, CssFont.WstParagraph2Font),
  ),
  discountNameColor: createStylesParam(
    'discountNameColor',
    colorParam(StyleType.color, 'color-8', CssColor.WstButtonPrimaryColor),
  ),
});
